
import React, { useState, useEffect } from 'react';
import DepartmentFilter from './DepartmentFilter';
import CalendarDetailedView from './CalendarDetailedView';
import { isGreekHoliday, getLeaveTypeInfo } from '../utils/vacationUtils';

const MANAGER_DEFAULT_DEPARTMENTS = {
  7:  [2, 4, 6, 7, 14, 15, 16],
  28: [21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 39],
  19: [5],
};

const DeptPills = ({ departments, selectedDepartments }) => {
  const [expanded, setExpanded] = useState(false);
  const INITIAL_SHOW = 5;

  const selected = departments.filter(d => selectedDepartments.includes(d.id));

  if (selectedDepartments.length === 0) {
    return (
      <div className="mb-4 flex items-center gap-2">
        <span className="text-xs font-medium text-gray-500 dark:text-jewel-text-muted flex-shrink-0">Τμήματα:</span>
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400">
          Κανένα τμήμα επιλεγμένο
        </span>
      </div>
    );
  }

  const visible = expanded ? selected : selected.slice(0, INITIAL_SHOW);
  const hidden  = selected.length - INITIAL_SHOW;

  return (
    <div className="mb-4 flex items-start gap-2 flex-wrap">
      <span className="text-xs font-medium text-gray-500 dark:text-jewel-text-muted flex-shrink-0 mt-1">Τμήματα:</span>
      <div className="flex flex-wrap gap-1.5">
        {visible.map(dept => (
          <span key={dept.id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border border-gray-200 dark:border-jewel-border bg-white dark:bg-jewel-bg text-gray-700 dark:text-jewel-text">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: dept.color }} />
              {dept.name}
          </span>
        ))}
        
        {!expanded && hidden > 0 && (
          <button
            onClick={() => setExpanded(true)}
            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border border-blue-200 dark:border-jewel-teal bg-blue-50 dark:bg-jewel-teal/20 text-blue-600 dark:text-jewel-teal-tint hover:bg-blue-100 dark:hover:bg-jewel-teal/30 transition-colors"
          >
            +{hidden} ακόμη…
          </button>
        )}
        {expanded && hidden > 0 && (
          <button
            onClick={() => setExpanded(false)}
            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border border-gray-200 dark:border-jewel-border bg-gray-50 dark:bg-jewel-surface text-gray-500 dark:text-jewel-text hover:bg-gray-100 dark:hover:bg-jewel-surface transition-colors"
          >
            Λιγότερα ↑
          </button>
        )}
      </div>
    </div>
  );
};

const CalendarView = ({ 
  vacationRequests = [], 
  userDatabase = [], 
  departments = [], 
  onRequestTimeOff,
  onRequestDecision,
  currentUser,
  holidays = [],
  isViewer = false,
}) => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const canSeeManagerCalendar = currentUser?.role === 'manager' || isViewer;

  useEffect(() => {
  if (!departments || departments.length === 0) return;
  if (selectedDepartments.length > 0) return;

  const explicitDepts = MANAGER_DEFAULT_DEPARTMENTS[currentUser?.id];
  if (explicitDepts) {
    const valid = explicitDepts.filter(id => departments.some(d => d.id === id));
    setSelectedDepartments(valid.length > 0 ? valid : departments.map(d => d.id));
  } else {
    const managedDeptIds = [...new Set(
      userDatabase
        .filter(u => (u.manager_id || u.managerId) === currentUser.id)
        .map(u => u.department_id || u.departmentId)
        .filter(Boolean)
    )];
    setSelectedDepartments(
      managedDeptIds.length > 0 ? managedDeptIds : departments.map(d => d.id)
    );
  }
}, [departments]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 653);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getFilteredVacationRequests = () => {
    if (!vacationRequests || !Array.isArray(vacationRequests)) {
      return [];
    }
    
    if (!currentUser) {
      return [];
    }
    
    if (canSeeManagerCalendar) {
      return vacationRequests.filter(req => {
        const userId = req.user_id || req.userId;
        const user = userDatabase.find(u => u.id === userId);
        const userDeptId = user?.department_id || user?.departmentId;
        const visibleStatus = ['approved', 'manager1_approved', 'pending'];
        return user && selectedDepartments.includes(userDeptId) && visibleStatus.includes(req.status);
      });
    } else {
      return vacationRequests.filter(req => {
        const userId = req.user_id || req.userId;
        const visibleStatus = ['approved', 'manager1_approved', 'pending'];
        return userId === currentUser.id && visibleStatus.includes(req.status);
      });
    }
  };

  const filteredVacationRequests = getFilteredVacationRequests();

  const getCalendarData = () => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();

    let startingDayOfWeek = firstDay.getDay();
    startingDayOfWeek = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    
    const days = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const vacationsOnThisDay = filteredVacationRequests.filter(req => {
        const startDate = new Date(req.start_date || req.startDate);
        const endDate = new Date(req.end_date || req.endDate);
        const currentDate = new Date(dateStr);
        
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        currentDate.setHours(0, 0, 0, 0);
        
        return currentDate >= startDate && currentDate <= endDate;
      });
      
      const holiday = isGreekHoliday(dateStr, holidays);
      
      days.push({
        day,
        date: dateStr,
        vacations: vacationsOnThisDay,
        holiday
      });
    }
    
    return { 
      days, 
      monthName: firstDay.toLocaleDateString('el-GR', { month: 'long', year: 'numeric' }) 
    };
  };

  const { days, monthName } = getCalendarData();
  const dayNames = ['Δευτέρα', 'Τρίτη', 'Τετάρτη', 'Πέμπτη', 'Παρασκευή', 'Σάββατο', 'Κυριακή'];

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(prevYear => prevYear + 1);
    } else {
      setCurrentMonth(prevMonth => prevMonth + 1);
    }
  };

  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(prevYear => prevYear - 1);
    } else {
      setCurrentMonth(prevMonth => prevMonth - 1);
    }
  };

  const getDepartmentByUserId = (userId) => {
    const user = userDatabase.find(u => u.id === userId);
    if (!user) return null;
    
    const departmentId = user.department_id || user.departmentId;
    return departments.find(dept => dept.id === departmentId);
  };

  if (!currentUser) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-jewel-text-muted">
        <p>Φόρτωση...</p>
      </div>
    );
  }

  if (isMobile) {
    const upcomingLeaves = filteredVacationRequests
      .filter(req => new Date(req.start_date || req.startDate) >= today)
      .sort((a, b) => {
        const dateA = new Date(a.start_date || a.startDate);
        const dateB = new Date(b.start_date || b.startDate);
        return dateA - dateB;
      });

    return (
      <div>
        <div className="flex items-center justify-between mb-6 xs:flex-col xs:gap-4">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-jewel-text">
            {canSeeManagerCalendar ? 'Επερχόμενες Άδειες Ομάδας' : 'Οι Επερχόμενες Άδειες μου'}
          </h2>
          <button
            onClick={onRequestTimeOff}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            + Νέα Άδεια
          </button>
        </div>

        {upcomingLeaves.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 dark:bg-jewel-bg rounded-lg">
            <p className="text-gray-500 dark:text-jewel-text-muted">Δεν υπάρχουν επερχόμενες εγκεκριμένες άδειες</p>
          </div>
        ) : (
          <div className="space-y-3">
            {upcomingLeaves.map(leave => {
              const userId = leave.user_id || leave.userId;
              const user = userDatabase.find(u => u.id === userId);
              const department = getDepartmentByUserId(userId);
              const leaveType = leave.leave_type || leave.leaveType || 'vacation';
              const leaveTypeInfo = getLeaveTypeInfo(leaveType);
              const startDate = new Date(leave.start_date || leave.startDate);
              const endDate = new Date(leave.end_date || leave.endDate);
              const daysUntil = Math.ceil((startDate - today) / (1000 * 60 * 60 * 24));

              return (
                <div 
                  key={leave.id}
                  className="bg-white dark:bg-jewel-bg border-l-4 rounded-lg p-4 shadow-sm"
                  style={{ borderLeftColor: leaveTypeInfo.color }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      {canSeeManagerCalendar && (
                        <div className="flex items-center space-x-2 mb-1">
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                            style={{ backgroundColor: department?.color || '#3B82F6' }}
                          >
                            {(user?.name || '').split(' ').map(n => n[0]).join('')}
                          </div>
                          <span className="font-medium text-gray-800 dark:text-jewel-text">{user?.name}</span>
                        </div>
                      )}
                      <div 
                        className="inline-flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium mb-2"
                        style={{ 
                          backgroundColor: `${leaveTypeInfo.color}20`,
                          color: leaveTypeInfo.color 
                        }}
                      >
                        <span>{leaveTypeInfo.icon}</span>
                        <span>{leaveTypeInfo.label}</span>
                      </div>
                    </div>
                    {daysUntil === 0 ? (
                      <span className="px-2 py-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-xs rounded-full font-medium">
                        Σήμερα!
                      </span>
                    ) : daysUntil <= 7 ? (
                      <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400 text-xs rounded-full font-medium">
                        Σε {daysUntil} ημέρες
                      </span>
                    ) : null}
                  </div>
                  
                  <div className="text-sm text-gray-600 dark:text-jewel-text">
                    <p className="font-medium">
                      {startDate.toLocaleDateString('el-GR', { day: 'numeric', month: 'long' })} - {endDate.toLocaleDateString('el-GR', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-jewel-text-muted mt-1">{leave.reason}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={goToPreviousMonth}
            className="bg-calm-green-gradient-25 dark:bg-none dark:bg-jewel-surface text-black dark:text-jewel-text font-semibold px-4 py-2 rounded-lg hover:bg-gray-400 dark:hover:bg-jewel-surface transition-colors"
          >
            {'<'} Προηγούμενος Μήνας
          </button>
          
          <h2 className="text-xl font-semibold text-gray-800 dark:text-jewel-text">{monthName}</h2>

          <button
            onClick={goToNextMonth}
            className="bg-calm-green-gradient-25 dark:bg-none dark:bg-jewel-surface text-black dark:text-jewel-text font-semibold px-4 py-2 rounded-lg hover:bg-gray-400 dark:hover:bg-jewel-surface transition-colors"
          >
            Επόμενος Μήνας {'>'}
          </button>
        </div>

        <div className="flex items-center space-x-4">
          {canSeeManagerCalendar && (
            <DepartmentFilter
              departments={departments}
              selectedDepartments={selectedDepartments}
              onDepartmentChange={setSelectedDepartments}
              className="min-w-[200px]"
            />
          )}
          
          <button
            onClick={onRequestTimeOff}
            className="flex items-center space-x-2 bg-calm-green-gradient-25 dark:bg-none dark:bg-jewel-surface text-black dark:text-jewel-text font-semibold px-4 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-jewel-forest transition-colors whitespace-nowrap"
          >
            <span>Ζητήστε Άδεια</span>
          </button>
        </div>
      </div>
      
      {canSeeManagerCalendar && (
        <DeptPills
          departments={departments}
          selectedDepartments={selectedDepartments}
        />
      )}

      {!canSeeManagerCalendar && (
        <div className="mb-4 p-3 bg-blue-50 dark:bg-jewel-teal/20 border border-blue-200 dark:border-jewel-teal rounded-lg">
          <p className="text-sm text-blue-800 dark:text-jewel-teal-tint">
            Εμφανίζονται μόνο οι δικές σας εγκεκριμένες άδειες
          </p>
        </div>
      )}

      {canSeeManagerCalendar ? (
        <CalendarDetailedView
          filteredVacationRequests={filteredVacationRequests}
          userDatabase={userDatabase}
          departments={departments}
          holidays={holidays}
          currentUser={currentUser}
          currentMonth={currentMonth}
          currentYear={currentYear}
          goToPreviousMonth={goToPreviousMonth}
          goToNextMonth={goToNextMonth}
          monthName={monthName}
          onRequestDecision={onRequestDecision}
        />
      ) : (
      <div className="bg-white dark:bg-jewel-bg border border-gray-200 dark:border-jewel-border rounded-lg overflow-hidden">
        <div className="grid grid-cols-7 bg-calm-green-gradient-25 dark:bg-none dark:bg-jewel-surface">
          {dayNames.map(day => (
            <div key={day} className="p-3 text-center text-sm font-medium text-gray-700 dark:text-jewel-text border-r border-gray-200 dark:border-jewel-border last:border-r-0">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((day, index) => {
            const MAX_VISIBLE = 2;
            const overflow = day ? Math.max(0, day.vacations.length - MAX_VISIBLE) : 0;
            return (
            <div 
              key={index} 
              className={`min-h-[100px] p-2 border-r border-b border-gray-300 dark:border-jewel-border last:border-r-0 ${
                day?.holiday ? 'bg-red-50 dark:bg-red-900/20' : ''
              }`}
            >
              {day && (
                <>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`font-medium ${day.holiday ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-jewel-text'}`}>
                      {day.day}
                    </span>
                    {day.holiday && (
                      <span className="text-red-500 dark:text-red-400 text-xs" title={day.holiday.holiday_name}>
                        🎉
                      </span>
                    )}
                  </div>
                  {day.holiday && (
                    <div className="text-xs text-red-600 dark:text-red-400 font-medium mb-1 truncate" title={day.holiday.holiday_name}>
                      {day.holiday.holiday_name}
                    </div>
                  )}
                  {day.vacations.slice(0, MAX_VISIBLE).map(vacation => {
                    const userId = vacation.userId || vacation.user_id;
                    const user = userDatabase.find(u => u.id === userId);
                    const leaveType = vacation.leave_type || vacation.leaveType || 'vacation';
                    const leaveTypeInfo = getLeaveTypeInfo(leaveType);
                    const status = vacation.status;
                    
                    const displayName = canSeeManagerCalendar 
                      ? (user?.name || user?.user_name)
                      : 'Η άδειά μου';

                    const isPending = status === 'pending';
                    const isPartial = status === 'manager1_approved';

                    return (
                      <div
                        key={vacation.id}
                        className="text-xs px-2 py-1 rounded mb-1 truncate flex items-center space-x-1"
                        style={{ 
                          backgroundColor: isPending ? '#FEF3C7' : isPartial ? '#DBEAFE' : leaveTypeInfo.color,
                          color: isPending ? '#92400E' : isPartial ? '#1E40AF' : 'white',
                          border: (isPending || isPartial) ? `1px dashed ${isPending ? '#F59E0B' : '#3B82F6'}` : 'none',
                          opacity: isPending ? 0.85 : 1,
                        }}
                        title={`${displayName} - ${leaveTypeInfo.label} - ${status === 'pending' ? 'Εκκρεμεί' : status === 'manager1_approved' ? 'Μερική Έγκριση' : 'Εγκρίθηκε'}`}
                      >
                        <span>{isPending ? '⏳' : isPartial ? '🔄' : leaveTypeInfo.icon}</span>
                        <span className="flex-1 truncate">{displayName}</span>
                      </div>
                    );
                  })}
                  {overflow > 0 && (
                    <div className="text-[10px] font-semibold text-gray-500 dark:text-jewel-text bg-gray-100 dark:bg-jewel-surface rounded px-1.5 py-0.5 text-center cursor-default"
                      title={day.vacations.slice(MAX_VISIBLE).map(v => {
                        const u = userDatabase.find(u => u.id === (v.user_id || v.userId));
                        return u?.name || 'Άγνωστος';
                      }).join(', ')}>
                      +{overflow} ακόμη
                    </div>
                  )}
                </>
              )}
            </div>
            );
          })}
        </div>
      </div>
      )}
      
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-jewel-bg p-4 rounded-lg border border-gray-200 dark:border-jewel-border">
          <h4 className="font-medium text-gray-700 dark:text-jewel-text text-sm">
            {canSeeManagerCalendar ? 'Εγκεκριμένες Μήνα' : 'Εγκρίσεις μου τον Μήνα'}
          </h4>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
            {filteredVacationRequests.filter(req => {
              const date = new Date(req.start_date || req.startDate);
              return date.getMonth() === currentMonth && date.getFullYear() === currentYear && req.status === 'approved';
            }).length}
          </p>
        </div>
        <div className="bg-white dark:bg-jewel-bg p-4 rounded-lg border border-gray-200 dark:border-jewel-border">
          <h4 className="font-medium text-gray-700 dark:text-jewel-text text-sm">Μερικώς Εγκρ. (Δ1)</h4>
          <p className="text-2xl font-bold text-blue-600 dark:text-jewel-teal-tint">
            {filteredVacationRequests.filter(req => {
              const date = new Date(req.start_date || req.startDate);
              return date.getMonth() === currentMonth && date.getFullYear() === currentYear && req.status === 'manager1_approved';
            }).length}
          </p>
        </div>
        <div className="bg-white dark:bg-jewel-bg p-4 rounded-lg border border-gray-200 dark:border-jewel-border">
          <h4 className="font-medium text-gray-700 dark:text-jewel-text text-sm">Εκκρεμείς Αιτήσεις</h4>
          <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
            {vacationRequests.filter(req => {
              const userId = req.user_id || req.userId;
              if (canSeeManagerCalendar) {
                const user = userDatabase.find(u => u.id === userId);
                const userDeptId = user?.department_id || user?.departmentId;
                return selectedDepartments.includes(userDeptId) && req.status === 'pending';
              } else {
                return userId === currentUser.id && req.status === 'pending';
              }
            }).length}
          </p>
        </div>
        <div className="bg-white dark:bg-jewel-bg p-4 rounded-lg border border-gray-200 dark:border-jewel-border">
          <h4 className="font-medium text-gray-700 dark:text-jewel-text text-sm">Αργίες Μήνα</h4>
          <p className="text-2xl font-bold text-red-600 dark:text-red-400">
            {holidays.filter(h => {
              const hDate = new Date(h.holiday_date);
              return hDate.getMonth() === currentMonth && hDate.getFullYear() === currentYear;
            }).length}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;